/**
 * Text helpers shared by the Argus chat + session-list surfaces.
 *
 * Outbound user messages can carry up to two LLM-only prefixes that must
 * be stripped before showing the body to humans:
 *
 *   1. `<!-- argus:sender ... -->\n\n` — prepended by the gateway
 *      (`be/argus-ts/src/server.ts`) so the agent knows who's talking in
 *      org-shared sessions. See `parseSenderBlock` in `argus-user.ts`.
 *   2. `[Context: ...]\n\n` — prepended by `use-argus-connection.ts` so
 *      the agent knows which page the user is on when they message from
 *      the sidebar.
 *
 * The order matters: the sender block is added by the backend *after*
 * the context prefix, so on the wire the sender block appears first.
 * `stripSendablePrefixes` removes both in the correct order.
 */

import { parseSenderBlock } from "./argus-user";
import { ONBOARDING_WELCOME_SENTINEL } from "./constants";

const CONTEXT_PREFIX_RE = /^\[Context:[^\]]*\]\n\n/;
// The onboarding-welcome sentinel is embedded in the seed `first_message` so
// the transcript can hide that row. It must not leak into visible titles
// (session-list item, header breadcrumb). Strip it + any trailing whitespace.
const ONBOARDING_SENTINEL_PREFIX_RE = new RegExp(
  `^${ONBOARDING_WELCOME_SENTINEL.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*`,
);

export function stripContextPrefix(msg: string | null | undefined): string {
  if (!msg) return "";
  return msg.replace(CONTEXT_PREFIX_RE, "").replace(ONBOARDING_SENTINEL_PREFIX_RE, "");
}

/**
 * Strip both the sender-block HTML comment and the `[Context: ...]`
 * prefix from a message body. Use this everywhere a queued/inbound user
 * message is rendered to humans (queue panel, search indices, previews).
 */
export function stripSendablePrefixes(msg: string | null | undefined): string {
  if (!msg) return "";
  const { body } = parseSenderBlock(msg);
  return stripContextPrefix(body);
}
